import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { IconPlayerPlay, IconRefresh, IconCurrencyTaka } from '@tabler/icons-react';
import { useDisclosure, useMediaQuery } from '@/lib/hooks';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { useAuthStore } from '../../store/authStore';
import { useIncomeExpenseStore } from '../../store/incomeExpenseStore';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import styles from './SupportWidget.module.scss';

/**
 * Clears a tool page's sticky action bar when one is on screen, so these floats
 * rest on top of it instead of over the tool's own controls. The variable is
 * published by the bar itself (see useStickyBarLift) and absent everywhere else.
 */
const FLOAT_BOTTOM = 'calc(var(--tool-action-bar-h, 0px) + 20px)';

export default function SupportWidget() {
    const { t } = useTranslation();
    const { isAuthenticated, user, updateSettings } = useAuthStore();
    const location = useLocation();
    const [videoOpened, { open: openVideo, close: closeVideo }] = useDisclosure(false);
    const { isOpen: isIEOpen, toggleModal: toggleIncomeExpense, openCentered: openIECentered, closeModal: closeIE } = useIncomeExpenseStore();
    const isMobile = useMediaQuery('(max-width: 768px)');

    // Ctrl / Cmd + I global shortcut to toggle income/expense modal
    const showIEBtn = user?.settings?.showIncomeExpenseBtn !== false;
    useEffect(() => {
        const handler = (e) => {
            if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 'i') {
                e.preventDefault();
                if (showIEBtn) {
                    toggleIncomeExpense();
                } else if (isIEOpen) {
                    closeIE();
                } else {
                    openIECentered();
                }
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [showIEBtn, isIEOpen, toggleIncomeExpense, openIECentered, closeIE]);

    const [eraserOpen, setEraserOpen] = useState(false);
    useEffect(() => {
        const onOpen  = () => setEraserOpen(true);
        const onClose = () => setEraserOpen(false);
        window.addEventListener('eraser-modal-open',  onOpen);
        window.addEventListener('eraser-modal-close', onClose);
        return () => {
            window.removeEventListener('eraser-modal-open',  onOpen);
            window.removeEventListener('eraser-modal-close', onClose);
        };
    }, []);

    // Allow mobile sidebar to open the video modal via custom event
    useEffect(() => {
        const handler = () => {
            openVideo();
            updateSettings({ videoPlayed: true }).catch(() => {});
        };
        window.addEventListener('sohozkaj:open-video', handler);
        return () => window.removeEventListener('sohozkaj:open-video', handler);
    }, []);

    const RELOAD_KEY = 'sohozkaj_reload_shown';
    const todayStr   = new Date().toISOString().slice(0, 10);
    const [showReloadBtn, setShowReloadBtn] = useState(false);
    useEffect(() => { setShowReloadBtn(localStorage.getItem(RELOAD_KEY) !== todayStr); }, []);

    const handleHardReload = () => {
        localStorage.setItem(RELOAD_KEY, todayStr);
        setShowReloadBtn(false);
        window.location.reload(true);
    };

    if (location.pathname.startsWith('/mobile-upload')) return null;
    if (location.pathname.startsWith('/admin/lexical-editor')) return null;
    if (!isAuthenticated || !user || (user.role !== 'OWNER' && user.role !== 'OPERATOR')) return null;

    const hideFloatingBtns = eraserOpen
        || (isMobile && location.pathname.startsWith('/generate-image'))
        // Immersive bulk-photo editor (the bare path is now the editor) + detail.
        // The project list moved to My Files, where the floats stay available.
        || location.pathname.startsWith('/bulk-photo-edit');

    const videoPlayed = user.settings?.videoPlayed === true;

    const handleOpenVideo = () => {
        openVideo();
        if (!videoPlayed) {
            updateSettings({ videoPlayed: true }).catch(() => {});
        }
    };

    // IE button is always leftmost (slot 0); video button stacks to its right
    const STEP = 52, BASE = 10;
    const ieLeft    = showIEBtn ? BASE : null;
    const videoLeft = !videoPlayed ? BASE + (showIEBtn ? STEP : 0) : null;
    const reloadLeft = null; // hidden for now

    return (
        <>
            {/* Hard-reload button — hidden for now
            {showReloadBtn && !hideFloatingBtns && (
                <div
                    className="fixed z-[100] transition-[bottom] duration-300 ease-out"
                    style={{ bottom: FLOAT_BOTTOM, left: reloadLeft }}
                >
                    <button
                        type="button"
                        title="নতুন আপডেট এসেছে, আপডেট পেতে ক্লিক করুন"
                        onClick={handleHardReload}
                        className={`
                            w-10 h-10 rounded-full flex items-center justify-center
                            bg-teal-500 hover:bg-teal-600 text-white
                            focus:outline-none transition-colors duration-600
                            ${styles.reloadButton}
                        `}
                    >
                        <IconRefresh size={20} />
                    </button>
                </div>
            )}
            */}

            {/* Intro video button — desktop only; mobile uses sidebar */}
            {!videoPlayed && !hideFloatingBtns && !isMobile && (
                <div
                    className="fixed z-[100] transition-[bottom] duration-300 ease-out"
                    style={{ bottom: FLOAT_BOTTOM, left: videoLeft }}
                >
                    <button
                        type="button"
                        title={t('support.introduceVideo')}
                        onClick={handleOpenVideo}
                        className={`
                            w-10 h-10 rounded-full flex items-center justify-center
                            bg-red-600 hover:bg-red-700 text-white
                            focus:outline-none transition-colors duration-600
                            ${styles.videoButton}
                        `}
                    >
                        <IconPlayerPlay size={22} />
                    </button>
                </div>
            )}

            {/* Income / Expense button — desktop only; mobile uses sidebar */}
            {showIEBtn && !hideFloatingBtns && !isMobile && (
                <div
                    className="fixed z-[100] transition-[bottom] duration-300 ease-out"
                    style={{ bottom: FLOAT_BOTTOM, left: ieLeft }}
                >
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button
                                type="button"
                                onClick={toggleIncomeExpense}
                                className={`
                                    w-10 h-10 rounded-full flex items-center justify-center
                                    text-white focus:outline-none transition-colors duration-600
                                    ${isIEOpen ? 'bg-orange-700' : 'bg-orange-600 hover:bg-orange-700'}
                                    ${styles.ieButton}
                                `}
                            >
                                <IconCurrencyTaka size={20} />
                            </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" sideOffset={8}>
                            <div>{t('incomeExpense.btnTooltip')}</div>
                            <div className="text-xs opacity-70 mt-0.5">Ctrl + I</div>
                        </TooltipContent>
                    </Tooltip>
                </div>
            )}

            {/* Video modal */}
            <Dialog open={videoOpened} onOpenChange={(open) => { if (!open) closeVideo(); }}>
                <DialogContent
                    className={isMobile ? 'w-[95vw] max-w-[95vw] p-3' : 'w-[60vw] max-w-[60vw] p-3'}
                >
                    <DialogHeader className="mb-2">
                        <DialogTitle>{t('support.introduceVideo')}</DialogTitle>
                    </DialogHeader>
                    <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
                        <iframe
                            src={videoOpened ? 'https://www.youtube-nocookie.com/embed/uzXwgR7-5eQ?rel=0' : ''}
                            title="Help Video"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                border: 'none',
                                borderRadius: '12px',
                            }}
                        />
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}